<template>
  <b-form v-if="!loading" @submit="onSubmit" novalidate class="invoice-wrap gk-input-light--delete">

    <div class="invoice-section">
      <div class="invoice-section--label">{{ $gettext('Correction details') }}</div>
      <div class="is-shadow-box p-3">
        
        <b-form-row class="gk-input-light--delete">
          <b-col cols="12" lg="6">
            <b-form-group label-class="gk-label-margin gk-modal-label" :label="$gettext('Name*')" label-for="inv-corr-name">
              <b-form-input data-analytics-click id="inv-corr-name" v-model="correction.name" :state="isValid('correction_name')" required :placeholder="$gettext('Correction')" />
              <b-form-invalid-feedback :state="isValid('correction_name')">
                {{ $gettext('This field is required.') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col cols="12" lg="6">
            <b-form-group label-class="gk-label-margin gk-modal-label" :label="$gettext('Period*')" label-for="inv-corr-period">
              <PeriodSelector v-if="defaultPeriodSelector" :default-period="defaultPeriodSelector" id="inv-corr-period" @input="setNewCorrectionDate" />
            </b-form-group>
          </b-col>

          <b-col cols="12" lg="6">
            <b-form-group label-class="gk-label-margin gk-modal-label" label="Customer" label-for="inv-corr-customer">
              <CustomerSelect 
                id="inv-corr-customer"
                :preselect="preselectedCustomer"
                searchable allow-empty
                @selected-customer="selectedCustomer"
                @pre-selected-customer="selectedCustomer"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" lg="6">
            <b-form-group label-class="gk-label-margin gk-modal-label" :label="$gettext('Project')" label-for="input-10">
              <b-form-select v-model="invoice.project" :disabled="projectList.length === 0">
                <b-form-select-option v-for="project in projectList" :value="project.id" :key="project.id">
                  {{ project.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-form-row>

      </div>
    </div>

    <div class="invoice-section">
      <div class="invoice-section--lines">
        <div class="invoice-section--label">{{ $gettext('Correction lines') }}</div>
        <div class="invoice-lines-wrap is-shadow-box">
          <InvoiceLines 
            v-model="invoice.invoice_lines"
            :invoice="invoice"
            :show-error="isValid('invoice_lines') === false"
            ref="invoice-lines"
          />
        </div>
      </div>
    </div>

    <div class="invoice-section">
      <div class="invoice-section--label">{{ $gettext('Description') }}</div>
      <b-form-textarea v-model="invoice.message" id="inv-corr-description" rows="4" />
    </div>

    <div v-if="invoice.correction && invoice.correction.id" class="invoice-section">
      <div class="invoice-section--label">{{ $gettext('Attached documents') }}</div>
      <div class=" is-shadow-box" style="overflow: hidden;">
        <AttachedDocumentList 
          deletable 
          :object-id="invoice.correction.id" 
          :object-type="'Correction'"
          :document-list="correctionFiles"
        />
      </div>
    </div>

    <div class="invoice-section">
      <div class="invoice-section--label">{{ $gettext('Upload files') }}</div>
      <div class="p-3 is-shadow-box">
        <LoadingSpinner v-if="uploadingFiles" :display-text="$gettext('Uploading correction. This might take a while.')" />
        <div style="margin-bottom: 1rem; font-size: 1rem; display: flex; justify-content: space-between;">
          <div>{{ $gettext('You can add multiple files.') }}</div>
          <div>
            {{ $gettext('Total file size:') }} <span :class="{ 'text-danger': maxSizeExceeded }">{{ niceBytes(totalFileSize, 1000, true) }}</span> / {{ niceBytes(maxTotalFileSize*1000*1000, 1000, true) }}
          </div>
        </div>
        <FileUploader
          ref="file-uploader"
          showsize
          :file-size-max="maxTotalFileSize"
          @updated-queued-files="updateInvoiceFiles"
        />
        <div v-if="maxSizeExceeded" class="text-danger" style="font-size: 14px; padding: 0.5rem; margin-top: 1rem;">
          {{ $gettextInterpolate(maxSizeExceededText, { size: niceBytes(totalFileSize, 1000, true), max: maxTotalFileSize }) }}
        </div>
      </div>
    </div>

    <div class="invoice-section">
      <GekkoButton 
        v-if="!emitSubmit"
        type="submit"
        :text="$gettext('Add correction')" 
        icon="fa-solid fa-plus"
        :disabled="isSaving"
      />
    </div>

  </b-form>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapActions } from "vuex";
import { niceBytes } from "@/utils/";
import GekkoButton from "@/components/GekkoButton";
import FileUploader from "@/components/ui/FileUploader";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"

import CustomerSelect from "@/components/invoices/ui/CustomerSelect"

import PeriodSelector from "@/components/ui/PeriodSelector";
import InvoiceLines from "@/components/invoices/ui/InvoiceLines";

import AttachedDocumentList from "@/components/documents/AttachedDocumentList"

import InvoiceService from "@/store/services/invoice.service.js";
import CorrectionService from "@/store/services/correction.service.js";
import DocumentUpload from "@/store/services/document.upload.service.js";
import CustomerService from "@/store/services/customer.service.js";

export default {
  name: "EditCorrectionForm",
  components: {
    GekkoButton,
    FileUploader,
    LoadingSpinner,
    CustomerSelect,
    PeriodSelector,
    InvoiceLines,
    AttachedDocumentList
  },
  props: {
    transactionInvoice: {
      type: Object,
      default: () => {}
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    emitSubmit: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      invoice: null,
      correction: null,

      created_new_invoice: null,

      invoiceFiles: null,
      uploadingFiles: false,

      RETENTION_RATES: [15, 7, 2, 1, 0],

      fromInvoice: false,
      correctionName: null,

      defaultPeriodSelector: null,

      maxTotalFileSize: 10,

      projectList: [],
      loading: false,
      isSaving: false,
      isSubmitted: false,
      requiredFields: ['correction_name', 'invoice_lines'],

      preselectedCustomer: null,
      loadingFiles: false
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: "main/getCurrentCompany",
      currentUserAccessRights: 'main/getCurrentAccessRights',
      companyTaxRates: "invoices/getCompanyTaxRates",
      visitingCompanyId: 'main/getVisitingCompanyId',
      storeInvoice: 'invoices/getInvoice',
      correctionFiles: 'invoices/getCorrectionFiles',
    }),
    totalFileSize () {
      let totalFileSize = 0
      if (this.invoiceFiles) {
        for (var i = 0; i < this.invoiceFiles.length; i++) {
          totalFileSize += this.invoiceFiles[i].size;
        }
      }

      // TODO Existing files size calculation for edit correction 
      // if (this.existingDocumentList) {
      //   for (var i = 0; i < this.existingDocumentList.length; i++) {
      //     this.totalFileSize += this.existingDocumentList[i].totalFileSize;
      //   }
      // }

      return totalFileSize
    },
    invoiceTotalAmount () {
      let sum = 0
      if (this.invoice.invoice_lines) {
        for (var i = 0; i < this.invoice.invoice_lines.length; i++) {
          sum = sum + this.invoice.invoice_lines[i].amount;
        }
        sum = Math.round(sum * 100) / 100; //make sure we have only 2 decimals
      }
      return sum
    },
    maxSizeExceeded () {
      return (this.totalFileSize > (this.maxTotalFileSize*1000*1000))
    },
    maxSizeExceededText () {
      return this.$gettext('Total size of files (%{ size }) exceeds upload limit. Please make sure your uploads do not exceed %{ max } MB.')
    },
  },
  created () {
    this.setupData()
  },
  methods: {
    ...mapActions ({ 
      retrieveCorrectionFiles: "invoices/retrieveCorrectionFiles"
    }),

    /**
     * Init, prepares and sets invoice and lines default data
     */
    setupData () {
      this.loading = true

      if (this.isEdit && this.storeInvoice) {

        this.invoice = Object.assign({}, this.storeInvoice)
        this.correction = Object.assign({}, this.storeInvoice.correction)

        this.preselectedCustomer = { name: '', customer_id: null, project_id: null }
        if (this.invoice.recipient_customer) {
          this.preselectedCustomer.customer_id = this.invoice.recipient_customer.id
          this.preselectedCustomer.name = this.invoice.recipient_customer.name
        }
        
        // set correction period
        this.defaultPeriodSelector = {
          month: moment(this.invoice.invoice_date, "YYYY-MM-DD").month() + 1,
          year: moment(this.invoice.invoice_date, "YYYY-MM-DD").year()
        }

        if (this.invoice.project) {
          this.invoice.project = this.invoice.project.id
        }
    } else {
        const invoiceDate = new Date().format('Y-m-d')
        const now = new Date().format('Y-m-d H-i-s-u')

        this.invoice = {
          number: this.visitingCompanyId + '_' + now.toString(),
          invoice_date: invoiceDate,
          payment_term: 0,
          status: 's',
          get_display_status: 'Sent',
          status_short: 's',
          sender_company: this.visitingCompanyId,
          vat_reversed_charge: false,
          line_amount_is_gross: true,
          recipient_customer: null,
          invoice_lines: [],
          currency: 'EUR'
        }
        
        let correctionName = ''
        if (this.fromInvoice) {
          correctionName = this.$gettext('Correction for ') + this.fromInvoice
        } else {
          if (this.currentUserAccessRights.user_is_of_connected_accountant) {
            correctionName = this.$gettext('Accountant Correction')
          } else {
            correctionName = this.$gettext('Correction')
          }
        }

        if (this.transactionInvoice) {
          // set correction name
          correctionName = this.$gettext('Correction for ') + this.transactionInvoice.description

          // set correction period
          this.defaultPeriodSelector = {
            month: moment(this.transactionInvoice.date, "YYYY-MM-DD").month() + 1,
            year: moment(this.transactionInvoice.date, "YYYY-MM-DD").year()
          }

          // set correction amount
          const def_line = {
            order: 0,
            multiplier: 1,
            amount: this.transactionInvoice.amount,
            subtotal_amount: this.transactionInvoice.amount,
            tax_rate: this.currentCompany.default_tax_rate,
            description: ''
          }
          this.invoice.invoice_lines.push(def_line)
        } else {
          const today = new Date()
          
          this.defaultPeriodSelector = { 
            month: moment(today, "YYYY-MM-DD").month() + 1,
            year: moment(today, "YYYY-MM-DD").year()
          }
        }

        this.correction = {
          name: correctionName,
          timeframe: moment(invoiceDate, "YYYY-MM-DD").format('MMMM YYYY')
        }

        this.invoice.retention_tax_percentage = this.RETENTION_RATES[this.RETENTION_RATES.length-1]
        if (this.currentCompany.companysettings.default_invoice_retention) {
            this.invoice.retention_tax_percentage = this.currentCompany.companysettings.default_invoice_retention
        }

        // TODO No Customer key?

        // Customer.list({
        //     owner_company: vm.isAccountant ? vm.customerCompany : vm.owner_company,
        //     extended: 'True',
        //     is_archived: 'False'
        // }).then(function (response) {
        //     vm.customers = {};
        //     for (var i = 0; i < response.data.length; i++) {
        //         vm.customers[response.data[i].id] = response.data[i];
        //     }
        //     vm.customers[0] = {id: 0, name: gettext("No Customer")};
        //     vm.invoice.recipient_customer = vm.customers[0];
        //     if (vm.edit) {
        //         var params = {
        //             extended: 'True'
        //         };
        //         vm.retrieveInvoice($state.params.id, params)
        //     }
        // });

      }
      this.loading = false
    },
    /**
     * Sets a new 'invoice.invoice_date' and 'correction.timeframe'
     * 'invoice.invoice_date' is set to last day of the selected month
     * 'correction.timeframe' format is 'MMMM YYYY' (e.g. 'March 2023')
     * @param {object} newDate new selected date via PeriodSelector component
     */
    setNewCorrectionDate (newDate) {
      this.invoice.invoice_date = new Date(newDate.to.addDays(-1)).format('Y-m-d');
      this.correction.timeframe = moment(this.invoice.invoice_date, "YYYY-MM-DD").format('MMMM YYYY')
    },
    /**
     * Starts save process. Default submit form prevented
     */
    onSubmit (e = null) {
      if (!this.isSaving) {
        this.isSaving = true
        this.isSubmitted = true
        if (e) e.preventDefault()

        if (!this.validateLines()) {
          this.$store.commit('setAlert', {
            title: this.$gettext('Could not save'),
            text: this.$gettext('Please review correction line amounts'),
            variant: 'danger',
            duration: 4
          })
          this.isSaving = false
          this.isSubmitted = false
          return
        }
        
        if(this.isEdit) {
          this.editCorrection()
        } else {
          this.createCorrection()
        }
      }
    },
    /**
     * Checks if a field is valid or empty/invalid if form is not yet submitted
     * 
     * @param {string} field field identifier on this.requiredFields array
     * @returns {boolean} false if field is invalid, null for valid (bootstrap :state prop contextual state)
     */
    isValid (field) {
      let isValid
      switch (field) {
        case 'correction_name':
          isValid = (this.correction.name && this.correction.name != '') ? null : false
          break;
        case 'invoice_lines':
          isValid = true
          break;
      }
      if (!this.isSubmitted) isValid = null
      return isValid
    },
    validateLines () {
      let valid_lines = true
      if (this.invoice.invoice_lines.length === 1 && this.invoice.invoice_lines[0].default) {
        valid_lines = false
      } else {
        this.invoice.invoice_lines.forEach(line => {
          if (!line.default && line.amount === '') {
            valid_lines = false
          }
        })
      }
      return valid_lines
    },
    /**
     * Checks validates this.requiredFields with isValid method
     * 
     * @returns {boolean} false if any field is empty or invalid
     */
    formIsValid () {
      let formErrors = []
      this.requiredFields.forEach(field => {
        formErrors.push(this.isValid(field))
      })
      return !formErrors.some((x) => x === false)
    },
    /**
     * Creates a paid invoice via InvoiceService
     *    then creates a correction via CorrectionService
     *    then uploads any files via DocumentUpload
     *    after that redirects to corresponding page
     */
    createCorrection () {
      if (this.formIsValid()) {
        this.prepareInvoiceLines()
        this.invoice.total_amount = this.invoiceTotalAmount

        if (this.invoice.recipient_customer) {
          this.correction.no_customer = false 
          this.invoice.recipient_customer = this.invoice.recipient_customer.id
        } else {
          this.correction.no_customer = true 
          this.invoice.recipient_customer = this.visitingCompanyId
        }

        this.invoice.is_correction = true
        InvoiceService.createPaidInvoice(this.invoice).then((response) => {
          this.created_new_invoice = response.data
          this.correction.invoice = response.data.id;

          CorrectionService.create(this.correction).then((response) => {
            this.bulkUpload(response.data)
          }).catch((error) => {
            this.isSaving = false
            this.uploadingFiles = false
            // TODO Upload error handling

            // init();
            // if (response.status!=400){
            //     this.uploadError = response.data['detail'];
            // }else{
            //     this.errors = response.data;
            //     this.uploadError = gettext("Error when uploading. Please make sure this file type is allowed and correct shown errors.");
            // }

          });
        })
      } else {
        this.isSaving = false
      }
    },
    editCorrection () {
      if (this.formIsValid()) {
        this.prepareInvoiceLines()
        this.invoice.total_amount = this.invoiceTotalAmount

        if (this.invoice.recipient_customer) {
          this.invoice.recipient_customer = this.invoice.recipient_customer.id
          this.correction.no_customer = false 
        } else {
          this.correction.no_customer = true 
          this.invoice.recipient_customer = this.visitingCompanyId
        }

        InvoiceService.update(this.invoice.id, this.invoice).then(() => {
          CorrectionService.update(this.invoice.correction.id, this.correction).then((response) => {
            this.bulkUpload(response.data)
          }).catch((error) => {
            this.uploadingFiles = false
            console.log('Correction update error', error)
          });
        })
        
      } else {
        this.isSaving = false
      }
    },
    bulkUpload (attached_to) {
      // TODO Mixpanel send create event

      // mixpanel.track("Correction: create", {
      //     'url': location.href
      // });

      const description = this.$gettext('document uploaded for invoice %(name)s');
      const uploadData = {
        attached_to: attached_to,
        files: this.invoiceFiles,
        company_profile: this.visitingCompanyId,
        description: description.replace("%(name)s", attached_to.name)
      }
      if (this.invoiceFiles && this.invoiceFiles.length > 0) {
        this.uploadingFiles = true
        DocumentUpload.bulkUpload(uploadData).then(() => {
          this.uploadingFiles = false
          if (this.isEdit) {
            this.$emit('updated');
          } else {
            this.$emit('created', this.created_new_invoice);
          }
          this.isSaving = false
        })
      } else {
        this.isSaving = false
        if (this.isEdit) {
            this.$emit('updated');
          } else {
            this.$emit('created', this.created_new_invoice);
          }
      }
    },
    /**
     * Removes the last item from this.invoice.invoice_lines array
     * which is suposed to be just a placeholder
     * Also removes subtotal_amount amount from all invoice_lines (neccesary?)
     */
    prepareInvoiceLines () {
      this.invoice.invoice_lines.pop()
    },
    /**
     * Gets Dropzone Files from fileUploader component and sets this.invoiceFiles
     */
    updateInvoiceFiles () {
      this.invoiceFiles = this.$refs['file-uploader'].getDropzoneFiles()
    },
    /**
     * Alias for utils/niceBytes
     */
    niceBytes (size, base, unit = false) {
      return niceBytes(size, base, unit)
    },
    /**
     * Gets customer's project list when a customer is selected
     * if none, projectList is emptied and invoice.project set to null
     */
    selectedCustomer (selected) {
      if (selected) {
        CustomerService.retrieve(selected.customer_id, { extended: true }).then((response) => {
          this.invoice.recipient_customer = response.data

          if (this.preselectedCustomer) {
            this.preselectedCustomer = null
          } else {
            this.invoice.project = null
          }
          
          this.projectList = response.data.projects
          if (this.projectList.length > 0) {
            this.projectList.unshift({ name: this.$gettext('Nothing Selected'), id: null })
          }        
        })
      } else {
        this.projectList.splice(0)
      }
    }
  }
}
</script>

<style lang="scss">
.upload-correction-form {
  display: flex;
  .gk-modal-label {
    margin-bottom: 0;
  }
  form { max-width: 900px; margin: 0 auto; }
}
</style>
